/**
 * FoodItemsDirectory — public search & listing for the Food Pulse
 * Global Food Knowledge Database. Now with a short topic-intro
 * article strip + sibling category tabs at the very top so a reader
 * arriving from a `Food` dropdown click sees the same rich landing
 * pattern every other FoodPulse section already uses.
 *
 * Route: /food-pulse/food
 *
 * URL param → article slug mapping lives in
 *   src/config/foodCategoryArticles.js  (shared with the seeder at
 *   backend/scripts/seed_food_category_articles.py).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Leaf, Utensils, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { FOOD_CATEGORY_TABS, tabForCategoryParam } from "@/config/foodCategoryArticles";

const API = process.env.REACT_APP_BACKEND_URL;

// Single-category chip strip. These fine-grained filters live UNDER
// the coarse sibling-tabs strip (which anchors to FOOD_CATEGORY_TABS).
const CATEGORIES = [
  { key: "",              label: "All"          },
  { key: "fruits",        label: "Fruits"       },
  { key: "vegetables",    label: "Vegetables"   },
  { key: "leafy-greens",  label: "Leafy greens" },
  { key: "herbs",         label: "Herbs"        },
  { key: "spices",        label: "Spices"       },
  { key: "grains",        label: "Grains"       },
  { key: "legumes",       label: "Legumes"      },
  { key: "nuts",          label: "Nuts"         },
  { key: "seafood",       label: "Seafood"      },
  { key: "fish",          label: "Fish"         },
  { key: "shellfish",     label: "Shellfish"    },
  { key: "meat",          label: "Meat"         },
  { key: "poultry",       label: "Poultry"      },
  { key: "dairy",         label: "Dairy"        },
  { key: "eggs",          label: "Eggs"         },
  { key: "fermented",     label: "Fermented"    },
  { key: "packaged",      label: "Packaged"     },
  { key: "beverages",     label: "Beverages"    },
];

export default function FoodItemsDirectory() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);
  const [articleLoading, setArticleLoading] = useState(false);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const tag = params.get("tag") || "";

  // Which of the 12 top-nav sibling tabs matches the current filter?
  const activeTab = useMemo(() => tabForCategoryParam(category), [category]);

  useEffect(() => {
    document.title = "Food Pulse — Global Food Knowledge Database";
  }, []);

  // Fetch food items ------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-items`, {
          params: { q: q || undefined, category: category || undefined, tag: tag || undefined, limit: 60 },
        });
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [q, category, tag]);

  // Fetch topic-intro article --------------------------------------
  useEffect(() => {
    if (!activeTab) { setArticle(null); return; }
    let cancelled = false;
    setArticleLoading(true);
    setArticle(null);
    (async () => {
      try {
        const res = await axios.get(`${API}/api/food-articles/${activeTab.slug}`);
        if (!cancelled) setArticle(res.data);
      } catch {
        if (!cancelled) setArticle(null);
      } finally {
        if (!cancelled) setArticleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);

  const setQ = (val) => {
    const next = new URLSearchParams(params);
    if (val) next.set("q", val); else next.delete("q");
    setParams(next, { replace: true });
  };
  const setCat = (val) => {
    const next = new URLSearchParams(params);
    if (val) next.set("category", val); else next.delete("category");
    setParams(next, { replace: true });
  };
  const clearTag = () => {
    const next = new URLSearchParams(params);
    next.delete("tag");
    setParams(next, { replace: true });
  };

  return (
    <div className="min-h-screen" style={{ background: "#F7FBF4" }} data-testid="food-items-directory">
      <SeoHead
        section="food-pulse"
        title={activeTab ? `${activeTab.label} — Food Pulse` : "Food Pulse — Global Food Knowledge Database"}
        description={article?.excerpt || "A comprehensive, standardised profile of every food item — nutrition, cultivation, recipes, cultural importance and environmental impact."}
      />

      {/* ── Hero (compact when a topic tab is active) ─────────────── */}
      <section className="border-b" style={{ borderColor: "#DCEAD1", background: "linear-gradient(180deg,#DCEAD1 0%,#F7FBF4 100%)" }}>
        <div className="max-w-6xl mx-auto px-4 py-10">
          {activeTab && (
            <Link to="/food-pulse/food"
                  className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest mb-3 hover:underline"
                  style={{ color: "#2F6B2F" }}
                  data-testid="food-back-all">
              <ArrowLeft className="w-3 h-3" /> Food Pulse · All Food
            </Link>
          )}
          <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest rounded-full px-3 py-1 mb-4"
               style={{ background: "#fff", border: "1px solid #B8D3A1", color: "#2F6B2F" }}>
            <Leaf className="w-3 h-3" /> Food Pulse · {activeTab?.label || "Global Food Knowledge"}
          </div>
          <h1 className="font-display font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl mb-3" style={{ color: "#1A3B1A" }}
              data-testid="food-hero-title">
            {activeTab ? activeTab.label : "Every food, one living reference."}
          </h1>
          <p className="text-base sm:text-lg max-w-3xl" style={{ color: "#3E5A3E" }}>
            {article?.excerpt ||
              "Explore verified profiles for fruits, vegetables, grains, seafood, dairy and beyond — with nutrition per 100 g, growing seasons, 25+ recipes each, and cultural notes drawn from USDA, FAO, WHO, NIH and university sources."}
          </p>

          <div className="mt-6 flex flex-wrap gap-3 items-center max-w-2xl">
            <div className="flex-1 min-w-[240px] flex items-center gap-2 rounded-full border bg-white px-4 py-2 shadow-sm"
                 style={{ borderColor: "#B8D3A1" }}>
              <Search className="w-4 h-4 opacity-60" />
              <input
                type="search"
                data-testid="food-search-input"
                placeholder="Search foods — tomato, mango, rice, saffron…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full bg-transparent outline-none text-sm"
              />
            </div>
            <div className="text-xs opacity-70" data-testid="food-total-count">
              {loading ? "Loading…" : `${total.toLocaleString()} item${total === 1 ? "" : "s"}`}
            </div>
            <Link to="/food-pulse/suggest-product"
                  className="text-xs px-3 py-1.5 rounded-full font-semibold text-white hover:shadow transition ml-auto"
                  style={{ background: "#0F3D2E" }}
                  data-testid="food-directory-suggest-cta">
              + Can't find it? Suggest a product
            </Link>
          </div>
        </div>
      </section>

      {/* ── Sibling tabs strip (12 Food-dropdown items) ─────────── */}
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <div className="flex flex-wrap gap-2" data-testid="food-tab-siblings">
          {FOOD_CATEGORY_TABS.map((tab) => {
            const active = activeTab?.slug === tab.slug;
            return (
              <Link
                key={tab.slug}
                to={`/food-pulse/food?category=${encodeURIComponent(tab.param)}`}
                data-testid={`food-tab-${tab.slug}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "text-white" : "hover:bg-white"}`}
                style={active
                  ? { background: "#2F6B2F", borderColor: "#2F6B2F" }
                  : { background: "#fff", borderColor: "#B8D3A1", color: "#1A3B1A" }}
              >
                {tab.label}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── Topic intro article (renders only when tab active) ── */}
      {activeTab && (
        <section className="max-w-4xl mx-auto px-4 pt-8" data-testid="food-topic-article">
          {articleLoading ? (
            <div className="text-sm opacity-70">Loading article…</div>
          ) : article?.body_html ? (
            <article
              className="prose prose-lg max-w-none prose-headings:font-display prose-p:text-[15px]"
              style={{ color: "#2A3A2A" }}
              dangerouslySetInnerHTML={{ __html: article.body_html }}
            />
          ) : (
            <div className="rounded-2xl border p-6 text-sm" style={{ borderColor: "#C79A3F", background: "#fff" }}>
              Introduction coming soon for <strong>{activeTab.label}</strong>. In the meantime, browse the item cards below.
            </div>
          )}
        </section>
      )}

      {/* ── Fine-grained chip strip ─────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 pt-6">
        <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-2">
          Refine by category
        </div>
        <div className="flex flex-wrap gap-2" data-testid="food-category-chips">
          {CATEGORIES.map((c) => {
            const active = (category || "") === c.key;
            return (
              <button
                key={c.key || "all"}
                type="button"
                onClick={() => setCat(c.key)}
                data-testid={`food-cat-${c.key || "all"}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "text-white" : "hover:bg-white"}`}
                style={active
                  ? { background: "#8B5E10", borderColor: "#8B5E10" }
                  : { background: "#fff", borderColor: "#E4DDD1", color: "#4A2E0F" }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
        {tag && (
          <div className="mt-3 text-xs">
            <span className="opacity-70">Filtered by tag:</span>{" "}
            <button onClick={clearTag} className="underline" data-testid="food-clear-tag">{tag} ✕</button>
          </div>
        )}
      </div>

      {/* ── Item grid ──────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-sm opacity-70" data-testid="food-loading">Loading foods…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border p-10 text-center" style={{ borderColor: "#B8D3A1", background: "#fff" }} data-testid="food-empty">
            <Sparkles className="w-6 h-6 mx-auto mb-2" style={{ color: "#2F6B2F" }} />
            <div className="font-display text-lg mb-1">No profiles yet for this filter.</div>
            <div className="text-sm opacity-70">
              An admin can generate profiles from the Food Pulse admin panel — or wait for the next batch to land.
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="food-grid">
            {items.map((f) => {
              const cover = (f.images || [])[0];
              const tags = (f.ai_metadata?.tags || []).slice(0, 3);
              return (
                <Link
                  key={f.slug}
                  to={`/food-pulse/food/${f.slug}`}
                  className="group rounded-2xl bg-white border overflow-hidden hover:shadow-md transition-shadow"
                  style={{ borderColor: "#B8D3A1" }}
                  data-testid={`food-card-${f.slug}`}
                >
                  {cover?.url ? (
                    <div className="aspect-[4/3] bg-black/5 overflow-hidden">
                      <img src={cover.url} alt={cover.caption || f.common_name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" loading="lazy" />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] flex items-center justify-center" style={{ background: "linear-gradient(135deg,#DCEAD1,#F7FBF4)" }}>
                      <Utensils className="w-8 h-8 opacity-30" style={{ color: "#2F6B2F" }} />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{f.category?.replace(/-/g, " ") || "food"}</div>
                    <div className="font-display text-lg mt-0.5 leading-tight">{f.common_name}</div>
                    {f.scientific_name && <div className="text-xs italic opacity-70 mt-0.5">{f.scientific_name}</div>}
                    {f.description && <div className="text-xs mt-2 opacity-80 line-clamp-2">{f.description}</div>}
                    {tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {tags.map((tg) => (
                          <span key={tg} className="text-[10px] font-mono px-1.5 py-0.5 rounded-full" style={{ background: "#DCEAD1", color: "#2F6B2F" }}>{tg}</span>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold group-hover:gap-2 transition-all" style={{ color: "#2F6B2F" }}>
                      Read the profile <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
